import { useState } from "react";
import { Form, required, useNotify, useRedirect, useTranslate } from "ra-core";
import { Layout } from "@/components/supabase/layout";
import type { FieldValues, SubmitHandler } from "react-hook-form";
import { TextInput } from "@/components/admin/text-input";
import { Button } from "@/components/ui/button";
import { getAuthRedirectUrl } from "@/components/atomic-crm/providers/supabase/authRedirect";
import { getSupabaseClient } from "@/components/atomic-crm/providers/supabase/supabase";

interface FormData {
  email: string;
}

export const ForgotPasswordPage = () => {
  const [loading, setLoading] = useState(false);

  const notify = useNotify();
  const redirect = useRedirect();
  const translate = useTranslate();

  const submit = async (values: FormData) => {
    try {
      setLoading(true);
      // Recovery links must round-trip through the static auth-callback.html
      // page: GoTrue appends the tokens as a URL fragment, which would clobber
      // a hash-router path like /#/set-password. The callback page forwards
      // type=recovery to #/set-password with the tokens as query params.
      const { error } = await getSupabaseClient().auth.resetPasswordForEmail(
        values.email,
        {
          redirectTo: getAuthRedirectUrl("/auth-callback.html"),
        },
      );
      if (error) {
        throw error;
      }
      redirect("/login?passwordRecoveryEmailSent=1");
    } catch (error: any) {
      // Map Supabase's terse rate-limit error to friendly guidance. The
      // built-in email service caps password-reset emails to a couple per
      // hour; surface that instead of the raw "email rate limit exceeded".
      const status = typeof error === "object" ? error?.status : undefined;
      const code = typeof error === "object" ? error?.code : undefined;
      const rawMessage =
        typeof error === "string" ? error : (error?.message ?? "");
      const isRateLimited =
        status === 429 ||
        code === "over_email_send_rate_limit" ||
        /rate limit/i.test(rawMessage);

      if (isRateLimited) {
        notify("crm.auth.reset_password_rate_limited", {
          type: "warning",
          messageArgs: {
            _: "Too many password reset emails have been requested recently. Please wait a few minutes and try again, or ask your administrator for an invite link.",
          },
        });
      } else {
        notify(
          typeof error === "string"
            ? error
            : typeof error === "undefined" || !error.message
              ? "ra.auth.sign_in_error"
              : error.message,
          {
            type: "warning",
            messageArgs: {
              _:
                typeof error === "string"
                  ? error
                  : error && error.message
                    ? error.message
                    : undefined,
            },
          },
        );
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="flex flex-col space-y-2 text-center">
        <h1 className="text-2xl font-semibold tracking-tight">
          {translate("ra-supabase.reset_password.forgot_password", {
            _: "Forgot password?",
          })}
        </h1>
        <p>
          {translate("ra-supabase.reset_password.forgot_password_details", {
            _: "Enter your email to receive a reset password link.",
          })}
        </p>
      </div>
      <Form<FormData>
        className="space-y-8"
        onSubmit={submit as SubmitHandler<FieldValues>}
      >
        <TextInput
          source="email"
          label={translate("ra.auth.email", {
            _: "Email",
          })}
          autoComplete="email"
          validate={required()}
        />
        <Button type="submit" className="cursor-pointer" disabled={loading}>
          {translate("crm.action.reset_password", {
            _: "Reset password",
          })}
        </Button>
      </Form>
    </Layout>
  );
};

ForgotPasswordPage.path = "forgot-password";
